import { useQueryClient } from '@tanstack/react-query'
import { CreditCard, Inbox, LayoutDashboard, LogOut, Route, ScrollText, Shield, Tag, Users } from 'lucide-react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { authClient } from '../lib/authClient'
import type { AuthedAdmin } from '../lib/session'
import CBBLogo from './cbb-logo'

const NAV = [
	{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
	{ to: '/users', label: 'Users', icon: Users },
	{ to: '/subscriptions', label: 'Subscriptions', icon: CreditCard },
	{ to: '/coupons', label: 'Coupons', icon: Tag },
	{ to: '/customer-journey', label: 'Customer Journey', icon: Route },
	{ to: '/support', label: 'Support inbox', icon: Inbox },
	{ to: '/logs', label: 'Logs', icon: ScrollText },
	{ to: '/admins', label: 'Admins', icon: Shield },
]

export function AppShell({ admin }: { admin: AuthedAdmin }) {
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	async function handleSignOut() {
		await authClient.signOut()
		// Drop every cached response — the next admin to sign in on this browser
		// must not briefly see the previous one's data.
		queryClient.clear()
		navigate('/login', { replace: true })
	}

	// The shell owns the viewport and never scrolls; only <main> does, so the
	// sidebar stays put however long the table gets.
	return (
		<div className="flex h-full flex-col overflow-hidden lg:flex-row">
			<aside className="flex shrink-0 flex-col border-b border-line bg-surface lg:h-full lg:w-60 lg:overflow-y-auto lg:border-b-0 lg:border-r">
				<div className="flex items-center gap-2.5 px-5 py-4">
					<CBBLogo monochrome className="h-8 w-auto" />
					<span className="rounded-md border border-line bg-canvas px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-ink-soft">
						Admin
					</span>
				</div>

				<nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-1 lg:flex-col lg:overflow-visible">
					{NAV.map(({ to, label, icon: Icon }) => (
						<NavLink
							key={to}
							to={to}
							className={({ isActive }) =>
								`flex items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition ${
									isActive ? 'bg-canvas text-ink' : 'text-ink-soft hover:bg-canvas'
								}`
							}
						>
							<Icon className="h-4 w-4" />
							{label}
						</NavLink>
					))}
				</nav>

				<div className="border-t border-line px-3 py-3">
					<p className="truncate px-2 pb-2 text-xs text-ink-soft" title={admin.email}>
						{admin.email}
					</p>
					<button type="button" onClick={handleSignOut} className="btn-secondary w-full justify-start">
						<LogOut className="h-4 w-4" />
						Sign out
					</button>
				</div>
			</aside>

			{/* No top padding: PageHeader supplies it, so it can sit flush against
			    the scrollport edge once it sticks. */}
			<main className="min-w-0 flex-1 overflow-y-auto px-5 pb-5 lg:px-8 lg:pb-8">
				<Outlet />
			</main>
		</div>
	)
}
